import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowForward } from '@mui/icons-material';
import { ReactComponent as Logo } from '../assets/logo.svg';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from || '/';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left side - Login form */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 50%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 4, sm: 8, md: 10 },
          py: 6,
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ maxWidth: 440, width: '100%' }}>
          <Logo style={{ width: 180, height: 'auto', marginBottom: 48 }} />

          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Log in
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Use your sandbox credentials to access the documentation
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              placeholder="Username"
              fullWidth
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2.5 }}
              autoFocus
              variant="outlined"
            />

            <TextField
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3.5 }}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              endIcon={!loading && <ArrowForward />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                textTransform: 'none',
                backgroundColor: '#4D2FA3',
                borderRadius: 1.5,
                '&:hover': { backgroundColor: '#3d2582' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Log in'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Right side - Branding panel */}
      <Box
        sx={{
          flex: '0 0 50%',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(160deg, #2a1458 0%, #120a2e 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative curved lines */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.15,
            background:
              'radial-gradient(ellipse at 30% 80%, #7c4dff 0%, transparent 50%), radial-gradient(ellipse at 70% 20%, #7c4dff 0%, transparent 50%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '140%',
            height: '140%',
            top: '-20%',
            left: '-20%',
            border: '1px solid rgba(124, 77, 255, 0.15)',
            borderRadius: '50%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '120%',
            height: '120%',
            bottom: '-30%',
            right: '-30%',
            border: '1px solid rgba(124, 77, 255, 0.12)',
            borderRadius: '50%',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 6, maxWidth: 480 }}>
          <Typography
            variant="h3"
            sx={{
              color: '#fff',
              fontWeight: 700,
              mb: 2,
            }}
          >
            Finfactor Docs
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 400,
              mb: 4,
            }}
          >
            Everything you need to integrate,&nbsp;in&nbsp;one&nbsp;place
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              textAlign: 'left',
            }}
          >
            {[
              { title: 'Seamless Integration', desc: 'Step-by-step guides to get up and running quickly' },
              { title: 'Full API Visibility', desc: 'Explore every endpoint with detailed request and response schemas' },
              { title: 'Flow Descriptions', desc: 'Understand consent, data, and authentication flows end to end' },
            ].map((item) => (
              <Box key={item.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#7c4dff',
                    mt: 1,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
